import { MouseEvent } from "react";
import { TableCell } from "@mui/material";
import { useTickets } from "~/pages/tickets/hooks";
import {
  AggregateScoreType,
  GetAffectedAssetsDocument,
  GetAggregateScoresDocument,
  GetCasesCountDocument,
  GetCveAdvisoriesDocument,
  GetCvEsDocument,
  GetFindingsDocument,
  ScoreRating,
} from "~/operations";
import { isSpaceOrWorkspaceScope, ScopeType, useScope } from "~/hooks/useScope";
import { useExceptions } from "~/components/exceptions/use-exceptions";
import { TriageBarProps } from "~/components/TriageBar/TriageBar";
import { CVEFindingsNode } from "~/pages/inventory/components/AssetSoftwareDetails/AssetSoftwareVulnerabilities/hooks/types";
import { CreateTicketDialog } from "~/pages/tickets/components/CreateTicketDialog";
import { useDialogs } from "@toolpad/core";
import {
  ExceptionFormInput,
  SetExceptionDialog,
} from "~/components/exceptions/SetExceptionDialog";
import { RemoveExceptionsGroupDialog } from "~/components/exceptions/RemoveExceptionsGroupDialog";
import {
  AggregateScoresEdges,
  AggregateScoresNode,
} from "~/components/FirewatchPage";
import {
  isAdvisoryAggregateScore,
  isAdvisoryFinding,
  isAdvisoryOrCveFinding,
  isCheckAggregateScore,
  isCheckFinding,
} from "~/pages/findings/FindingOnAsset/utils";
import { useSelection } from "~/hooks/useSelection";
import { useTicketingIntegrations } from "~/pages/tickets/hooks/useTicketingIntegrations";
import { SelectedEntityWithException } from "~/components/exceptions/types";
import { AdvisoryFindingsNode } from "~/pages/inventory/components/Findings/FindingsTable/utils/advisoryTypes";
import { CheckFindingsCheckNode } from "~/pages/inventory/asset/types";
import { FindingNode } from "~/pages/findings/FindingOnAsset/types";
import {
  getFindingMrn,
  isAggregateScoresNode,
} from "~/pages/inventory/components/Findings/FindingsTable/utils/findingTableUtils";
import { useSecurityPipelineIntegrations } from "~/pages/integrations/security-pipeline/hooks/useSecurityPipelineIntegrations";
import {
  allowedFindingTypes,
  isRemediationWorkflowSupported,
} from "~/pages/inventory/components/Findings/FindingsTable/utils/isRemediationWorkflowSupported";
import { TakeActionButton } from "~/components/TakeAction/TakeActionButton";
import { useFeatureFlags } from "~/providers/FeatureFlagsProvider";
import { CreateSecurityPipelinePrDialog } from "~/pages/integrations/security-pipeline/components/CreatePullRequest/CreateSecurityPipelinePRDialog";

import { ExposedAsset } from "~/pages/space/vulnerabilities/components/exposed-assets/types";
import { isAffectedAsset } from "~/pages/space/vulnerabilities/components/exposed-assets/utils";

const getExceptionTarget = (scoreType: AggregateScoreType | undefined) => {
  if (scoreType === AggregateScoreType.Check) {
    return "check";
  }

  if (scoreType === AggregateScoreType.Advisory) {
    return "advisory";
  }

  return "cve";
};

type TriageCellProps = {
  finding:
    | CVEFindingsNode
    | AdvisoryFindingsNode
    | CheckFindingsCheckNode
    | AggregateScoresNode
    | ExposedAsset;
  type?: "small-table" | "action-cell";
  scopeMrn?: string;
};

export const FindingTableTriageCell = ({
  finding,
  type,
  scopeMrn,
}: TriageCellProps) => {
  const { isFeatureEnabled } = useFeatureFlags();
  const { spaceScope, activeScope } = useScope();
  const dialogs = useDialogs();

  const { setSelectedEntities } = useSelection<SelectedEntityWithException>();

  const {
    handleCreateTicketingIntegrationModalOpen,
    loading: createCaseLoading,
    handleCreateCase,
    hasCreateTicketPermissions,
  } = useTickets({
    createCaseRefetchQueries: [GetCasesCountDocument],
  });

  const findingMrn = getFindingMrn(finding);

  const checkFinding =
    isCheckAggregateScore(finding as AggregateScoresNode) ||
    isCheckFinding(finding as FindingNode);
  const advisoryFinding =
    isAdvisoryAggregateScore(finding as AggregateScoresNode) ||
    isAdvisoryFinding(finding as FindingNode);

  const scoreType = checkFinding
    ? AggregateScoreType.Check
    : AggregateScoreType.Advisory;

  const target = getExceptionTarget(scoreType);

  // the title of the triage modal
  // i.e. CVE-2025-1234
  let dialogTitle = finding.id || "this finding";

  if (isAggregateScoresNode(finding)) {
    if (finding.scoreType === AggregateScoreType.Check) {
      // i.e. Ensure root group is empty
      dialogTitle = finding.title || "";
    } else {
      // i.e. ALAS-2025-1234
      dialogTitle = finding.findingMrn.split("/").pop() || "";
    }
  } else {
    if (finding.__typename === "CheckFinding") {
      dialogTitle = finding.title || "";
    }
  }

  const {
    handleRemoveExceptionGroups,
    handleSetException,
    loading: exceptionsLoading,
  } = useExceptions({
    onSetException: () => {
      setSelectedEntities([]);
    },
    scopeMrns: [scopeMrn || activeScope?.mrn || ""],
    ...(advisoryFinding ? { advisoryMrns: [findingMrn] } : {}),
    ...(checkFinding ? { queryMrns: [findingMrn] } : {}),
    ...(!checkFinding && !advisoryFinding ? { cveMrns: [findingMrn] } : {}),
    ...(type === "action-cell"
      ? {
          refetchQueries: [
            GetCvEsDocument,
            GetAggregateScoresDocument,
            GetCveAdvisoriesDocument,
            GetAffectedAssetsDocument,
            ...(isAggregateScoresNode(finding) ? [] : [GetFindingsDocument]),
          ],
        }
      : { refetchQueries: [GetCvEsDocument] }),
  });

  const handleSetExceptionModalOpen = () => {
    dialogs.open(SetExceptionDialog, {
      target,
      selectedEntities: [
        {
          mrn: findingMrn,
          rating: finding?.rating || ScoreRating.Unscored,
          title: finding?.title || "",
        },
      ],
      onSave: (params: ExceptionFormInput) => {
        return handleSetException(params);
      },
      targetData: {
        targetTitle: dialogTitle,
        ...(!isAggregateScoresNode(finding) && {
          onAsset: {
            id: finding?.id || "",
            mrn: isAffectedAsset(finding)
              ? finding?.assetMrn || ""
              : finding?.asset?.mrn || "",
            name: finding?.title || "",
            __typename: "EntityInfoAsset",
          },
        }),
      },
    });
  };

  const handleRemoveExceptionModalOpen = () => {
    dialogs.open(RemoveExceptionsGroupDialog, {
      onRemove: async (params) => {
        await handleRemoveExceptionGroups(params);
        setSelectedEntities([]);
      },
      exceptionMrns: [
        finding?.exception && "exceptionMrn" in finding.exception
          ? finding.exception.exceptionMrn
          : undefined,
      ].filter((mrn): mrn is string => Boolean(mrn)),
    });
  };

  const handleCreateTicketDialogOpen = () => {
    if (!spaceScope || !activeScope || !isSpaceOrWorkspaceScope(activeScope))
      return;

    dialogs.open(CreateTicketDialog, {
      isCombinedTicket: true,
      target: "asset",
      loading: createCaseLoading,
      onSave: (data) =>
        handleCreateCase({
          data,
          refetchQueries: isAggregateScoresNode(finding)
            ? [GetAggregateScoresDocument]
            : [GetFindingsDocument],
        }),
      selectedEntities: [
        {
          mrn: findingMrn || "",
          scopeMrn: activeScope?.mrn || "",
        },
      ],
      space: spaceScope,
      scope: activeScope,
      hasCreateCasePermissions: hasCreateTicketPermissions,
    });
  };

  const { hasTicketingIntegration } = useTicketingIntegrations();
  const { hasSecurityPipelineIntegrations } = useSecurityPipelineIntegrations();

  const handleSecurityPipelinePRDialogOpen = () => {
    if (!spaceScope) return;

    if (!isRemediationWorkflowSupported(finding)) return;

    if (isAggregateScoresNode(finding)) {
      const aggScoresEdges: AggregateScoresEdges = [
        {
          __typename: "AggregateScoreEdge",
          node: finding,
          cursor: "",
        },
      ];
      return dialogs.open(CreateSecurityPipelinePrDialog, {
        aggregateScoresEdges: aggScoresEdges,
        spaceScope: spaceScope,
      });
    }

    if (
      allowedFindingTypes.includes(finding.__typename) &&
      isAdvisoryOrCveFinding(finding)
    ) {
      const findingNode: Array<AdvisoryFindingsNode | CVEFindingsNode> = [
        finding,
      ];
      return dialogs.open(CreateSecurityPipelinePrDialog, {
        findings: findingNode,
        spaceScope: spaceScope,
        ...(finding.asset?.mrn ? { assetMrns: [finding.asset.mrn] } : {}),
      });
    }
  };

  const availableRemediations: TriageBarProps["availableRemediations"] = {
    ticket: {
      active: true,
      onClick: hasTicketingIntegration
        ? handleCreateTicketDialogOpen
        : handleCreateTicketingIntegrationModalOpen,
    },
    securityPipeline: {
      active:
        isRemediationWorkflowSupported(finding) &&
        isFeatureEnabled("Security Pipeline") &&
        hasSecurityPipelineIntegrations,
      onClick: handleSecurityPipelinePRDialogOpen,
    },
    exception: {
      active: spaceScope?.type === ScopeType.Space,
      hasException: !!finding?.exception,
      onClick: !!finding?.exception
        ? handleRemoveExceptionModalOpen
        : handleSetExceptionModalOpen,
      disabled: exceptionsLoading,
    },
  };

  return (
    <TableCell
      align={type === "action-cell" ? "left" : "right"}
      onClick={(e: MouseEvent<HTMLTableCellElement>) => e.stopPropagation()}
      sx={{ position: "relative", zIndex: 1 }}
    >
      <TakeActionButton
        availableRemediations={availableRemediations}
        type={type}
      />
    </TableCell>
  );
};
